/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Link } from "react-router-dom";
import { FileText, ArrowLeft } from "lucide-react";

export default function Terms() {
  return (
    <div className="max-w-[900px] mx-auto px-6 lg:px-12 py-24" data-testid="terms-page">
      <Link to="/" className="btn-ghost text-xs mb-8 inline-flex" data-testid="terms-back">
        <ArrowLeft size={12} /> Back Home
      </Link>
      <div className="label-eyebrow text-[var(--ff-red)] mb-2 flex items-center gap-2">
        <FileText size={12} /> ▮ LEGAL
      </div>
      <h1 className="font-display text-4xl lg:text-5xl leading-none mb-3">Terms of Service</h1>
      <p className="font-mono-ui text-[0.7rem] uppercase tracking-[0.2em] text-[var(--ff-text-dim)] mb-12">
        Last updated: February 26, 2026 · Effective date: February 26, 2026
      </p>

      <div className="prose-legal space-y-10">
        <Section n="1" title="Acceptance of Terms">
          <p>
            Welcome to Freshy Fresh Productions LLC ("<strong>Freshy Fresh</strong>", "<strong>we</strong>",
            "<strong>us</strong>", or "<strong>our</strong>"). These Terms of Service ("<strong>Terms</strong>")
            govern your access to and use of the website located at freshyfreshproductionsllc.net (the
            "<strong>Site</strong>"), the Freshy Fresh Discord server, our Discord bot, and any related
            services (collectively, the "<strong>Services</strong>").
          </p>
          <p>
            By creating an account, linking a Discord account, subscribing to a show membership, or
            otherwise using the Services, you agree to these Terms. If you do not agree, do not use
            the Services.
          </p>
          <p>You must be at least 13 years old to use the Services. If you are between 13 and 18, a
            parent or legal guardian must review and agree to these Terms on your behalf.</p>
        </Section>

        <Section n="2" title="What the Services Include">
          <ul>
            <li>
              <strong>Company Website.</strong> Public pages including Home, About, Departments, Brands, Team,
              Hosts, HR Disclosures, Careers/Applications, and a live Show Schedule with timezone conversion.
            </li>
            <li>
              <strong>Show Schedule.</strong> Weekly programming grid (Sunday–Saturday) with prime-time badges,
              morning/afternoon/late-night dayparts, YouTube integration, and show detail pages with ratings.
            </li>
            <li>
              <strong>Show Leaderboard.</strong> Public rankings combining YouTube view data and admin-entered
              report cards.
            </li>
            <li>
              <strong>Discord Sign-In (OAuth).</strong> Optional Discord OAuth login that syncs your Discord
              identity, avatar, server memberships, and role assignments to the Site.
            </li>
            <li>
              <strong>Fan Features.</strong> Watchlists, viewing streaks, DM reminders, custom notification
              preferences, and per-show "Fan Rating" feedback.
            </li>
            <li>
              <strong>Show Memberships.</strong> Optional paid monthly subscriptions per show, up to three
              admin-defined tiers per show, each with custom perks. Payments are processed by Stripe.
            </li>
            <li>
              <strong>Host Studio.</strong> Tools for hosts to view their earnings ledger, link a Stripe
              account, and receive automated monthly payouts computed from Showrunner-aware split rules.
            </li>
            <li>
              <strong>AI Assistant ("Freshy AI").</strong> A Discord slash command (`/ask`) and website chat
              widget powered by Claude Sonnet 4.5 that can answer questions about our network, schedule, and
              policies.
            </li>
            <li>
              <strong>Employee &amp; HR Tools.</strong> Employee accounts, HR case submission &amp; review,
              Applications intake, and internal reporting dashboards accessible to authorized staff only.
            </li>
            <li>
              <strong>Discord Security &amp; Moderation.</strong> Server blacklist enforcement, user blacklist,
              adjacency-based auto-bans (see Privacy Policy), ban appeals, moderator warnings, and role-based
              access control (Founder / Admin / Moderator / Employee / Host / User).
            </li>
          </ul>
        </Section>

        <Section n="3" title="Accounts &amp; Discord Linking">
          <p>
            Most fan-facing features require you to sign in with Discord. When you sign in, we ask Discord for
            your Discord ID, username, avatar, email address (if you granted it), and the list of Discord
            servers you belong to. This last piece is required so our automated moderation system (see the
            Privacy Policy) can enforce our safety rules.
          </p>
          <p>
            You are responsible for keeping your account secure. Do not share credentials, resell access, or
            create accounts on behalf of a banned person.
          </p>
          <p>
            You may unlink your Discord account, delete your Site profile, or re-link a different Discord
            account at any time via your Profile page. Re-linking resets your verification status.
          </p>
        </Section>

        <Section n="4" title="Acceptable Use">
          <p>You agree not to:</p>
          <ul>
            <li>Post, upload, or transmit content that is harassing, threatening, hateful, defamatory,
              obscene, or promotes illegal activity.</li>
            <li>Impersonate a Freshy Fresh employee, host, moderator, or another user.</li>
            <li>Attempt to gain unauthorized access to any part of the Services, another user's account,
              our servers, or connected third-party systems (Discord, Stripe, YouTube).</li>
            <li>Circumvent, disable, or otherwise interfere with security or moderation features, including
              our automated blacklist and ban system.</li>
            <li>Scrape, mirror, or resell content from the Services without our written permission.</li>
            <li>Use the Services to run competing operations designed to divert traffic from our shows or
              hosts.</li>
          </ul>
          <p>Violations may result in warnings, temporary bans, permanent bans, and/or termination of any
            active membership. See Section 8 for our appeal process.</p>
        </Section>

        <Section n="5" title="Show Memberships &amp; Payments">
          <p>
            Fan memberships are optional monthly subscriptions offered per show. Prices, tier names, and
            perks are set by Freshy Fresh Productions LLC and displayed on the show's detail page before
            you subscribe.
          </p>
          <ul>
            <li>All prices are shown in U.S. Dollars and are exclusive of any applicable taxes charged by
              Stripe on our behalf.</li>
            <li>Subscriptions renew monthly on the same day-of-month as your original signup, until you
              cancel.</li>
            <li>You may cancel at any time from your <Link to="/memberships" className="underline">Memberships</Link> page.
              Cancellation takes effect at the end of the current billing period; you keep access until then.</li>
            <li>All payments are processed by <strong>Stripe, Inc.</strong> We do not store your full card number.
              Stripe's terms and privacy policy apply to the payment portion of the transaction.</li>
            <li><strong>Refunds:</strong> Because memberships are digital and access is provided immediately, we
              do not offer refunds for partial months. If a technical error prevented you from accessing perks,
              contact us and we will make it right.</li>
            <li>Host payouts are computed monthly from the platform's Showrunner-aware split table
              (documented on the Host Studio) and delivered via Stripe Connect where enabled.</li>
          </ul>
        </Section>

        <Section n="6" title="Content Ownership &amp; License">
          <p>
            <strong>Our Content.</strong> All show content, branding, logos, and website copy are the property of
            Freshy Fresh Productions LLC or its licensors. You may view and share links to our public pages
            but may not reproduce or distribute them for commercial purposes without written permission.
          </p>
          <p>
            <strong>Your Content.</strong> Any content you submit (fan ratings, HR case descriptions, appeal text,
            comments, job applications) remains yours. By submitting it, you grant Freshy Fresh a
            non-exclusive, worldwide, royalty-free license to use, display, moderate, and back it up for the
            purpose of operating the Services. You represent that you have the right to grant that license.
          </p>
        </Section>

        <Section n="7" title="Discord Moderation, Blacklists &amp; Bans">
          <p>
            The Services include an automated moderation system that operates across the Site and our
            Discord server. In particular:
          </p>
          <ul>
            <li>We keep a <strong>server blacklist</strong> of Discord servers that violate our safety rules.
              Users who are members of a blacklisted server are automatically banned from ours.</li>
            <li>We keep a <strong>user blacklist</strong> of individual Discord users. Any user who shares a
              guild with a blacklisted user may be automatically banned under our adjacency rule.</li>
            <li>Bans are enforced by both the Site (`is_banned` flag on your account) and the Discord bot
              (guild-level ban).</li>
            <li>When you are auto-banned, our bot will DM you the reason and a link to the appeal form.</li>
            <li>You may submit an appeal from the <Link to="/banned" className="underline">Banned page</Link>. Approved
              appeals restore your Site access, lift the Discord ban, and (where possible) auto-rejoin you
              to the Discord server.</li>
          </ul>
          <p>
            <em>Why this exists:</em> our production network sits alongside a lot of communities that have
            targeted our hosts. The blacklist system is designed to prevent brigading, harassment
            coordination, and cross-server retaliation. It is not a judgment about who you are as a person;
            it's a network-hygiene rule.
          </p>
        </Section>

        <Section n="8" title="Appeal Rights">
          <p>
            If you believe a ban is incorrect, you may appeal via the Banned page. Appeals are reviewed by a
            human moderator within 30 days. If your appeal is denied, you may submit a new appeal after 30
            days have passed since your last submission (the "cooldown"). Repeated, low-effort appeals may
            be dismissed without review.
          </p>
          <p>
            Founders, Admins, and Moderators may lift bans manually at any time. Founders and Admins cannot
            be automatically banned.
          </p>
        </Section>

        <Section n="9" title="AI Assistant Disclaimer">
          <p>
            Our AI Assistant ("Freshy AI") is powered by third-party language models (Claude Sonnet 4.5 by
            Anthropic) and may occasionally generate inaccurate or incomplete information. Do not treat the
            assistant's responses as legal, medical, financial, or safety advice. When in doubt, contact a
            human moderator.
          </p>
          <p>
            Your conversations with the assistant may be temporarily retained (up to 24 hours) to provide
            conversational context and are not used to train third-party models.
          </p>
        </Section>

        <Section n="10" title="Third-Party Services">
          <p>The Services integrate with the following third parties, each of which has its own terms and privacy policies:</p>
          <ul>
            <li><strong>Discord Inc.</strong> — OAuth sign-in, bot, DMs, slash commands.</li>
            <li><strong>Stripe, Inc.</strong> — payment processing, subscription billing, host payouts.</li>
            <li><strong>YouTube (Google LLC)</strong> — video embeds and public view-count data.</li>
            <li><strong>Anthropic (Claude)</strong> — powers Freshy AI.</li>
            <li><strong>Emergent Platform</strong> — infrastructure, object storage, and Emergent-managed LLM key.</li>
          </ul>
        </Section>

        <Section n="11" title="Termination">
          <p>
            You may stop using the Services at any time. We may suspend or terminate your access if you
            violate these Terms, threaten the safety of the community, or fail to pay for an active
            subscription. Provisions that by their nature should survive termination will do so (including
            intellectual-property, liability, and disputes sections).
          </p>
        </Section>

        <Section n="12" title="Disclaimers &amp; Limitation of Liability">
          <p>
            The Services are provided <strong>as is</strong> and <strong>as available</strong> without warranties of
            any kind, express or implied. To the maximum extent permitted by law, Freshy Fresh Productions
            LLC disclaims all warranties including merchantability, fitness for a particular purpose, and
            non-infringement.
          </p>
          <p>
            To the maximum extent permitted by law, in no event will our aggregate liability to you for
            any claim arising out of the Services exceed the greater of (a) the total amount you paid us
            in the 12 months preceding the claim, or (b) US $50.00.
          </p>
        </Section>

        <Section n="13" title="Governing Law &amp; Disputes">
          <p>
            These Terms are governed by the laws of the State of Delaware, USA, without regard to its
            conflict-of-laws principles. You agree that any dispute arising out of or relating to the
            Services will be resolved in the state or federal courts located in Delaware, and you consent
            to the personal jurisdiction of those courts.
          </p>
        </Section>

        <Section n="14" title="Changes to These Terms">
          <p>
            We may update these Terms occasionally. When we make material changes, we will update the "Last
            updated" date at the top of this page and, when practical, post a notice on the Site or via
            Discord. Your continued use of the Services after changes take effect constitutes acceptance of
            the new Terms.
          </p>
        </Section>

        <Section n="15" title="Contact">
          <p>
            Questions about these Terms? Reach us at <strong>legal@freshyfreshproductionsllc.net</strong> or
            via a moderator in the Discord server.
          </p>
        </Section>

        <div className="pt-8 mt-8 border-t border-[var(--ff-border)] flex flex-wrap gap-4 items-center justify-between">
          <div className="font-mono-ui text-[0.7rem] uppercase tracking-[0.2em] text-[var(--ff-text-dim)]">
            © Freshy Fresh Productions LLC · All rights reserved
          </div>
          <Link to="/privacy" className="btn-ghost text-xs" data-testid="terms-to-privacy">
            Read the Privacy Policy →
          </Link>
        </div>
      </div>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section data-testid={`terms-section-${n}`}>
      <h2 className="font-display text-2xl mb-4 flex items-baseline gap-3">
        <span className="text-[var(--ff-red)] font-mono-ui text-sm tracking-[0.2em]">§{n}</span>
        <span dangerouslySetInnerHTML={{ __html: title }} />
      </h2>
      <div className="space-y-3 text-sm leading-relaxed text-[var(--ff-text)]">{children}</div>
    </section>
  );
}
